import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { Folder, FolderInput, Users } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Spinner } from "@/components/ui/spinner"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { queryKeys } from "@/lib/query-keys"
import { ProjectService } from "@/services/project-service"

type MoveToProjectDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessionId: string | undefined
  sessionType: string
  onProjectSelected: (projectId: string) => void
}

export function MoveToProjectDialog({
  open,
  onOpenChange,
  sessionId,
  sessionType,
  onProjectSelected,
}: MoveToProjectDialogProps) {
  const { t } = useTranslation()

  const { data: projects, isLoading } = useQuery({
    queryKey: queryKeys.projects(sessionType),
    queryFn: () => ProjectService.listProjects(sessionType),
    enabled: open,
  })

  const handleProjectClick = (projectId: string) => {
    if (!sessionId) return
    onProjectSelected(projectId)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderInput className="size-4" />{" "}
            {t("chatsession.move_to_project", { defaultValue: "Move to project" })}
          </DialogTitle>
          <DialogDescription>
            {t("chatsession.move_to_project_description", {
              defaultValue: "Select a project to move this chat into.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Spinner className="size-6" />
            </div>
          )}

          {!isLoading && (!projects || projects.length === 0) && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="bg-muted/50 mb-4 rounded-full p-4">
                <Folder className="text-muted-foreground/60 size-6" />
              </div>
              <p className="text-muted-foreground text-sm">
                {t("chatsession.no_projects_available", {
                  defaultValue: "No projects available. Create a project first.",
                })}
              </p>
            </div>
          )}

          {!isLoading && projects && projects.length > 0 && (
            <ScrollArea className="h-[min(400px,_55dvh)] pr-4">
              <div className="space-y-2">
                {projects.map((project) => {
                  const isShared = (project.participants?.length ?? 0) > 1
                  return (
                    <Button
                      key={project.id}
                      className="w-full justify-start"
                      variant="outline"
                      onClick={() => handleProjectClick(project.id)}
                    >
                      <Folder className="mr-2 size-4" />
                      <span className="flex-1 text-left">{project.name || project.id}</span>
                      {isShared && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-muted-foreground ml-2 flex items-center">
                                <Users className="size-3.5" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t("sharing.shared_with_count", {
                                count: (project.participants?.length ?? 1) - 1,
                                defaultValue: "Shared with {{count}} others",
                              })}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </Button>
                  )
                })}
              </div>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
